import $ = require("jquery");
import ol = require("openlayers");
import { cssin, mixin } from "ol3-fun/index";

const css = `
    .ol-input {
        position:absolute;
    }
    .ol-input.top {
        top: 0.5em;
    }
    .ol-input.left {
        left: 0.5em;
    }
    .ol-input.bottom {
        bottom: 0.5em;
    }
    .ol-input.right {
        right: 0.5em;
    }
    .ol-input.top.left {
        top: 4.5em;
    }
    .ol-input button {
        min-height: 1.375em;
        min-width: 1.375em;
        width: auto;
        display: inline;
    }
    .ol-input.left button {
        float:right;
    }
    .ol-input.right button {
        float:left;
    }
    .ol-input input {
        height: 24px;
        min-width: 240px;
        border: none;
        padding: 0;
        margin: 0;
        margin-left: 2px;
        margin-top: 1px;
        vertical-align: top;
    }
    .ol-input input.hidden {
        display: none;
    }
`;

let olcss = {
  CLASS_CONTROL: "ol-control",
  CLASS_UNSELECTABLE: "ol-unselectable",
  CLASS_UNSUPPORTED: "ol-unsupported",
  CLASS_HIDDEN: "ol-hidden"
};

export interface InputOptions {
  // what css class name to assign to the main element
  autoSearchDelay?: string;
  className?: string;
  expanded?: "true" | "false";
  autoCollapse?: "true" | "false";
  closedText?: string;
  openedText?: string;
  valueName?: string;
  source?: HTMLElement;
  target?: HTMLElement;
  // what to show on the tooltip
  placeholderText?: string;
  onChange?: (args: { value: string }) => void;
}

const expando = {
  right: "»",
  left: "«"
};

const defaults: InputOptions = {
  autoSearchDelay: "1",
  className: "ol-input bottom left",
  expanded: "false",
  autoCollapse: "true",
  closedText: expando.right,
  openedText: expando.left,
  placeholderText: "Search",
  valueName: "value"
};

export class Control extends ol.control.Control {
  static about = `
A collapsible input control.
    `;

  static options = {
    autoCollapse: {
      about: "Auto-collapse input control when user presses Enter",
      domain: "true|false",
      default: defaults.autoCollapse
    },
    autoSearchDelay: {
      about:
        "Number of seconds to wait for user input before auto-searching, 0 to disable auto searching",
      default: defaults.autoSearchDelay
    },
    className: {
      about: "The css class name to assign to the main element",
      default: defaults.className
    },
    closedText: "Text or tooltip to render when control is collapsed",
    expanded: {
      about: "'true' if the control is to be initially expanded",
      domain: "true|false",
      default: defaults.expanded
    },
    openedText: {
      about: "Text or tooltip to render when control is not collapsed",
      default: defaults.openedText
    },
    placeholderText: {
      about: "Input placeholder (text to render before a value is provided)",
      default: defaults.placeholderText
    },
    valueName: {
      about: "Places the value on this attribute",
      default: defaults.valueName
    }
  };

  static create(options?: InputOptions): Control {
    cssin("ol-input", css);

    // provide computed defaults
    options = mixin(
      {
        openedText:
          options.className && -1 < options.className.indexOf("left")
            ? expando.left
            : expando.right,
        closedText:
          options.className && -1 < options.className.indexOf("left")
            ? expando.right
            : expando.left
      },
      options || {}
    );

    // provide static defaults
    options = mixin(mixin({}, defaults), options);

    // stomps over the className???
    let element = <HTMLElement>options.element || document.createElement("div");
    element.className = `${options.className} ${olcss.CLASS_UNSELECTABLE} ${olcss.CLASS_CONTROL}`;

    let geocoderOptions = mixin(
      {
        element: element,
        target: options.target,
        expanded: false
      },
      options
    );

    return new Control(geocoderOptions);
  }

  private button: HTMLButtonElement;
  private input: HTMLInputElement;
  private prevInput: string;

  constructor(
    options: InputOptions & {
      element: HTMLElement;
      target: HTMLElement;
    }
  ) {
    // ol3-control converts strings to bool/num, undo that hack here
    "autoSearchDelay".split(",").forEach(k => {
      if (typeof options[k] === "number") options[k] = options[k].toString();
    });
    "expanded,autoCollapse".split(",").forEach(k => {
      if (typeof options[k] === "boolean")
        options[k] = options[k] ? "true" : "false";
    });

    let change = _.debounce(() => this.change(options), 100);

    if (!options.element) return Control.create(options);

    super({
      element: options.element,
      target: options.target
    });

    let button = (this.button = document.createElement("button"));
    button.setAttribute("type", "button");
    button.title = options.placeholderText;
    options.element.appendChild(button);

    let input = (this.input = document.createElement("input"));
    input.type = "text";
    input.placeholder = options.placeholderText;

    options.element.appendChild(input);

    button.addEventListener("click", () => {
      "true" === options.expanded
        ? this.collapse(options)
        : this.expand(options);
    });

    input.addEventListener("keypress", (args: KeyboardEvent) => {
      if (args.key === "Enter") {
        button.focus();
        "true" === options.autoCollapse && this.collapse(options);
        change();
      }
    });

    input.addEventListener("change", () => change());

    if (options.autoSearchDelay) {
      let delay = 1000 * parseFloat(options.autoSearchDelay);
      if (delay > 0) {
        input.addEventListener(
          "keyup",
          _.debounce(() => this.change(options), delay)
        );
      }
    }
    input.addEventListener("blur", () => change());

    "true" === options.expanded ? this.expand(options) : this.collapse(options);
  }

  dispose() {
  }

  collapse(options: InputOptions) {
    options.expanded = "false";
    this.input.classList.add("hidden");
    this.button.classList.remove("hidden");
    this.button.innerHTML = options.closedText;
  }

  expand(options: InputOptions) {
    options.expanded = "true";
    this.input.classList.remove("hidden");
    "true" === options.autoCollapse && this.button.classList.add("hidden");
    this.button.innerHTML = options.openedText;
    this.input.focus();
    this.input.select();
  }

  private change(options: InputOptions) {
    let input = this.input;
    if (!input.value) return;
    if (input.value === this.prevInput) return;
    this.prevInput = input.value;

    let args = {
      type: "change",
      value: input.value
    };

    // odd feature allowing the value to be placed on another attribute to avoid a remapping handler
    // and to avoid modifing handlers that expect "location" on the args stack.
    args[options.valueName] = input.value;

    this.dispatchEvent(args);
    if (options.onChange) options.onChange(args);
  }
}
